import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import type { ApprovalQueueEntry, ReopenView, TimesheetView } from "../../shared/types/api";
import { DecisionDrawer, ReopenDecisionDrawer } from "./TimesheetDrawers";
import { TimesheetHistoryCard } from "./TimesheetHistoryCard";
import { formatHoursAsTime, formatPeriod } from "./timesheetVocabulary";

/**
 * What is waiting on an approver (§81).
 *
 * <p>The difference column is the one a manager reads first, so it is shown as a signed figure
 * rather than left to be worked out from expected and logged. A month that is eleven hours short
 * is a conversation; the same month rendered as "160" beside "171" is arithmetic somebody does
 * thirty times.
 *
 * <p>Correction requests sit on the same page rather than a separate screen. They are the same
 * decision — whether a period should move — and splitting them means one of the two queues goes
 * unwatched.
 */
export function ApprovalQueuePage() {
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [deciding, setDeciding] = useState<{
    timesheet: TimesheetView;
    decision: "approve" | "reject";
  } | null>(null);
  const [reopening, setReopening] = useState<{
    request: ReopenView;
    decision: "approve" | "reject";
  } | null>(null);

  const queue = useQuery({
    queryKey: ["timesheets", "awaiting-me"],
    queryFn: () => api.get<ApprovalQueueEntry[]>("/api/v1/timesheets/awaiting-me"),
  });

  const reopens = useQuery({
    queryKey: ["timesheets", "reopen-requests"],
    queryFn: () => api.get<ReopenView[]>("/api/v1/timesheets/reopen-requests"),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["timesheets"] });
    void queryClient.invalidateQueries({ queryKey: ["timesheet"] });
  };

  const openDecision = async (entry: ApprovalQueueEntry, decision: "approve" | "reject") => {
    const timesheet = await api.get<TimesheetView>(`/api/v1/timesheets/${entry.timesheetId}`);
    setDeciding({ timesheet, decision });
  };

  const columns: Column<ApprovalQueueEntry>[] = [
    {
      key: "employeeName",
      header: "Person",
      render: (entry) => (
        <Link
          to={`/timesheets/${entry.timesheetId}`}
          className="font-medium text-ink-900 hover:text-brand-600"
        >
          {entry.employeeName}
        </Link>
      ),
    },
    {
      key: "periodStart",
      header: "Period",
      render: (entry) => formatPeriod(entry.periodStart, entry.periodEnd),
    },
    {
      key: "expectedHours",
      header: "Expected",
      className: "text-right tnum",
      render: (entry) => formatHoursAsTime(entry.expectedHours),
    },
    {
      key: "loggedHours",
      header: "Logged",
      className: "text-right tnum",
      render: (entry) => formatHoursAsTime(entry.loggedHours),
    },
    {
      key: "difference",
      header: "Difference",
      className: "text-right tnum",
      render: (entry) => {
        const difference = (entry.loggedHours ?? 0) - (entry.expectedHours ?? 0);
        if (Math.abs(difference) < 0.01) {
          return <span className="text-emerald-700">on expectation</span>;
        }
        return (
          <span className={difference < 0 ? "text-amber-700" : "text-ink-600"}>
            {difference > 0 ? "+" : "−"}
            {formatHoursAsTime(Math.abs(difference))}
          </span>
        );
      },
    },
    {
      key: "submittedAt",
      header: "Submitted",
      render: (entry) => new Date(entry.submittedAt).toLocaleDateString(),
    },
    {
      key: "actions",
      header: "",
      render: (entry) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" onClick={() => void openDecision(entry, "approve")}>
            Approve
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void openDecision(entry, "reject")}>
            Send back
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Timesheet approvals"
        subtitle="Periods submitted to you, and requests to reopen ones already settled"
      />

      <Card>
        <CardHeader title="Waiting for approval" />
        <CardBody className="p-0">
          {queue.isLoading ? (
            <LoadingState label="Loading the queue" />
          ) : queue.isError ? (
            <ErrorState error={queue.error} onRetry={() => void queue.refetch()} />
          ) : queue.data && queue.data.length > 0 ? (
            <DataTable columns={columns} rows={queue.data} rowKey={(entry) => entry.timesheetId} />
          ) : (
            <EmptyState
              title="Nothing waiting"
              message="Submitted timesheets appear here for whoever can approve them."
            />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Correction requests" />
        <CardBody className="p-0">
          {reopens.data && reopens.data.length > 0 ? (
            <ul className="divide-y divide-ink-100">
              {reopens.data.map((request) => (
                <li key={request.id} className="flex items-start justify-between gap-4 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-900">
                      {request.employeeName}{" "}
                      <span className="font-normal text-ink-500">
                        · {formatPeriod(request.periodStart, request.periodEnd)}
                      </span>
                    </p>
                    <p className="mt-0.5 text-sm text-ink-700">{request.reason}</p>
                    <p className="mt-0.5 text-xs text-ink-400">
                      Asked by {request.requestedByName} on{" "}
                      {new Date(request.requestedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge tone="amber">Waiting</StatusBadge>
                    <Button
                      size="sm"
                      onClick={() => setReopening({ request, decision: "approve" })}
                    >
                      Reopen
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setReopening({ request, decision: "reject" })}
                    >
                      Decline
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="No corrections requested"
              message="An approved period can only be changed by asking for it to be reopened (§78)."
            />
          )}
        </CardBody>
      </Card>

      {/* Gated on the permission, not on a role name, and the endpoint enforces it again —
          hiding the card is a courtesy, not the control. */}
      {can("TIMESHEET_VIEW_ALL") && <TimesheetHistoryCard />}

      {deciding && (
        <DecisionDrawer
          open
          decision={deciding.decision}
          timesheet={deciding.timesheet}
          onClose={() => setDeciding(null)}
          onSaved={refresh}
        />
      )}
      <ReopenDecisionDrawer
        open={reopening != null}
        request={reopening?.request ?? null}
        decision={reopening?.decision ?? "approve"}
        onClose={() => setReopening(null)}
        onSaved={refresh}
      />
    </div>
  );
}
